import { Controller } from "@hotwired/stimulus";

class WishlistGridController extends Controller {
  static targets = [
    "items",
    "sentinel",
    "results",
    "loading",
    "error",
    "consentWarning",
  ];
  static values = {
    productsPerPage: Number,
    resultsText: String,
  };

  alreadyLoaded = false;

  connect() {
    this.showLoading();
    // swym requires preferences processing consent allowed
    document.addEventListener("visitorConsentCollected", (event) => {
      this.checkConsentAndLoad();
    });
    window.Shopify?.loadFeatures?.(
      [{ name: "consent-tracking-api", version: "0.1" }],
      (error) => {
        if (error) {
          return;
        }
        this.checkConsentAndLoad();
      },
    );
  }

  checkConsentAndLoad() {
    const preferencesProcessingAllowed =
      window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.();
    if (!preferencesProcessingAllowed) {
      this.hideLoading();
      this.showConsentWarning();
      console.warn(
        "Impossibile caricare la wishlist: preferenze di privacy non consentite.",
      );
      return;
    }
    if (this.alreadyLoaded) {
      return;
    }
    this.alreadyLoaded = true;
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }
    window.SwymCallbacks.push(this.initSwymUI.bind(this));

    this.products = [];
    this.batchSize = this.productsPerPageValue;
    this.currentIndex = 0;
    this.observer = new IntersectionObserver(this.loadMore.bind(this), {
      rootMargin: "200px",
    });
  }

  initSwymUI(swatInstance) {
    this.swat = swatInstance;
    this.fetchWishlist();
  }

  setTextIfExists(selector, value) {
    const el = document.querySelector(selector);
    if (el) {
      el.textContent = value;
    }
  }

  fetchWishlist() {
    const urlParams = new URLSearchParams(window.location.search);
    const hkey = urlParams.get("hkey");
    if (hkey) {
      this.fetchSharedWishlist(hkey);
    } else {
      this.swat.fetchLists({
        callbackFn: (lists) => {
          const selectedList = lists.reverse()[0];
          this.products = selectedList.listcontents || [];
          this.updateResults();
          this.renderNextBatch();
        },
        errorFn: (error) => console.error("Error fetching Lists", error),
      });
    }
  }

  fetchSharedWishlist(hkey) {
    this.swat.fetchListDetails(
      {
        lid: hkey, // lid
      },

      // onSuccess (lambda inline)
      (listDetails) => {
        this.products = listDetails.items || [];
        this.updateResults();
        this.renderNextBatch();
        const fname = listDetails.list?.userinfo?.fname;
        const lname = listDetails.list?.userinfo?.lname;
        const em = listDetails.list?.userinfo?.em;

        this.setTextIfExists("[data-fname]", fname);
        this.setTextIfExists("[data-lname]", lname);
        this.setTextIfExists("[data-email]", em);

        if (fname || lname || em) {
          document
            .querySelector("[data-shareInfo]")
            .classList.remove("tw:hidden");
        }
      },

      // onError (lambda inline)
      (error) => {
        this.hideLoading();
        this.showError();
        console.error("Error while fetching List details", error);
      },
    );
  }

  async renderNextBatch() {
    this.showLoading();
    this.observer.unobserve(this.sentinelTarget);
    const nextProducts = this.products.slice(
      this.currentIndex,
      this.currentIndex + this.batchSize,
    );

    const fetches = nextProducts.map(async (item) => {
      const handle = item.du.split("/products/")[1];
      const res = await fetch(
        `/products/${handle}?section_id=wishlist-product`,
      );
      const html = await res.text();
      const start = html.indexOf(">") + 1;
      const end = html.lastIndexOf("</div>");
      return html.substring(start, end).trim() || "";
    });

    Promise.all(fetches).then((htmlArray) => {
      this.itemsTarget.insertAdjacentHTML("beforeend", htmlArray.join(""));
      this.currentIndex += this.batchSize;
      this.swat.initializeActionButtons("[data-controller='wishlist-grid']");
      this.hideLoading();

      if (this.currentIndex >= this.products.length) {
        this.observer.disconnect();
      } else {
        requestAnimationFrame(() => this.observer.observe(this.sentinelTarget));
      }
    });
  }

  updateResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.textContent = `${this.products.length} ${this.resultsTextValue}`;
    }
  }

  loadMore(entries) {
    if (entries.some((e) => e.isIntersecting)) {
      this.renderNextBatch();
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("tw:hidden");
    }
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("tw:hidden");
    }
  }

  showConsentWarning() {
    if (this.hasConsentWarningTarget) {
      this.consentWarningTarget.classList.remove("tw:hidden");
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("tw:hidden");
    }
  }
}

Stimulus.register("wishlist-grid", WishlistGridController);
